import { useEffect, useState } from 'react';

/**
 * Returns the latest `value` after `delay` ms have passed without further changes.
 *
 * When `delay` is `0` (or falsy) the hook behaves as a pass-through: it returns `value`
 * synchronously without scheduling a timer, so the no-delay case adds no extra render.
 *
 * Generic and side-effect free: it knows nothing about filters or any specific domain.
 *
 * @param value - The value to debounce.
 * @param delay - Debounce delay in milliseconds. Defaults to `0` (no debounce).
 * @returns The debounced value.
 */
const useDebouncedValue = <T>(value: T, delay = 0): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    if (!delay) {
      // Pass-through: keep the debounced value in sync with the source value.
      setDebouncedValue(value);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, delay]);

  return delay ? debouncedValue : value;
};

export default useDebouncedValue;
